/* eslint-disable react/prop-types */
import { useState } from "react";
import useFormData from "@/hooks/useFormData";
import { useSession } from "@/hooks/useSession";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import axios from "@/api/axios";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Chip,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";

function InsertComment({
  commentedDocument,
  commentModalOpen,
  setCommentModalOpen,
  onCommentAdded,
}) {
  const { postData } = useFormData();
  const { session, token } = useSession();
  const { userDetails } = useUsersSettingService(session?.id);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const clearFileSelection = () => {
    setSelectedFiles([]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = new FormData();
    const sentData = {
      comment: comment,
    };

    form.append(
      "data",
      new Blob([JSON.stringify(sentData)], { type: "application/json" })
    );
    // Append each selected file to FormData
    selectedFiles.forEach((file) => {
      form.append("attachments", file);
    });

    const url = `/api/v1/workpermits/${commentedDocument}/comments`;

    try {
      const response = await postData(form, url);
      console.log("Comment API response:", response);

      // Call the callback with the new comment data if provided
      if (onCommentAdded && response) {
        // If API returned just the comment ID (string), fetch the full comment
        if (typeof response === "string") {
          const commentId = response;

          try {
            // Fetch the full document to get the complete comment with attachments
            const documentResponse = await axios.get(
              `/api/v1/workpermits/${commentedDocument}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            // Find the newly created comment by ID
            const newComment = documentResponse.data.comments.find(
              (c) => c.id === commentId
            );

            if (newComment) {
              console.log("Fetched full comment with attachments:", newComment);
              onCommentAdded(newComment);
            } else {
              // Fallback: Create comment without attachments
              console.log("Could not find comment in document, using fallback");
              const authorData = {
                id: userDetails?.id || session?.id,
                username: userDetails?.username || session?.username,
                first_name:
                  userDetails?.first_name || session?.first_name || "",
                last_name: userDetails?.last_name || session?.last_name || "",
                avatar_url:
                  userDetails?.avatar_url || session?.avatar_url || null,
              };
              onCommentAdded({
                id: commentId,
                comment: comment,
                author: authorData,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                attachments: [],
              });
            }
          } catch (fetchError) {
            console.error("Error fetching full comment:", fetchError);
            // Fallback: Create comment without attachments
            const authorData = {
              id: userDetails?.id || session?.id,
              username: userDetails?.username || session?.username,
              first_name: userDetails?.first_name || session?.first_name || "",
              last_name: userDetails?.last_name || session?.last_name || "",
              avatar_url:
                userDetails?.avatar_url || session?.avatar_url || null,
            };
            onCommentAdded({
              id: commentId,
              comment: comment,
              author: authorData,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              attachments: [],
            });
          }
        } else {
          // API returned a full object
          const authorData = {
            id: userDetails?.id || session?.id,
            username: userDetails?.username || session?.username,
            first_name: userDetails?.first_name || session?.first_name || "",
            last_name: userDetails?.last_name || session?.last_name || "",
            avatar_url: userDetails?.avatar_url || session?.avatar_url || null,
          };

          const newComment = {
            ...response,
            comment: response.comment || comment,
            author: response.author || authorData,
            attachments: response.attachments || [],
          };
          console.log("Full comment object returned:", newComment);
          onCommentAdded(newComment);
        }
      }

      setToast({
        open: true,
        severity: "success",
        message: t("insertComment.successMessage"),
      });
      setCommentModalOpen(false);
      setComment(""); // Reset form
      clearFileSelection();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            width: "50%",
            maxHeight: 500,

            transition: "all 0.3s ease",
            transform: "perspective(1000px) rotateX(0deg)",
          },
        }}
        maxWidth="md"
        open={commentModalOpen}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: (theme) => theme.typography.h4.fontSize,
          }}
        >
          {t("insertComment.title")}
          <IconButton
            onClick={() => setCommentModalOpen(false)}
            style={{ float: "right" }}
            aria-label={t("insertComment.closeButtonAria")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            label={t("insertComment.commentLabel")}
            placeholder={t("insertComment.commentPlaceholder")}
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            variant="outlined"
            margin="dense"
            disabled={isSubmitting}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              fullWidth
              disabled={isSubmitting}
            >
              {t("insertComment.fileUploadLabel", "Upload Files")}
              <input
                type="file"
                hidden
                multiple
                accept="image/*,application/pdf"
                onChange={handleFileChange}
              />
            </Button>

            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {t("insertComment.selectedFiles", "Selected Files")} (
                  {selectedFiles.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => removeFile(index)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      size="small"
                      disabled={isSubmitting}
                      sx={{ maxWidth: "100%" }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onSubmit}
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{ mt: 2 }}
          >
            {isSubmitting
              ? t("insertComment.submitting", "Submitting...")
              : t("insertComment.submitButton")}
          </Button>
        </DialogActions>
      </Dialog>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </>
  );
}

export default InsertComment;
